import { useEffect } from "react";
import { ArrowLeft } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const LAST_UPDATED = "11 May 2026";

export default function Privacy() {
  useEffect(() => {
    document.title = "Privacy Policy — Zevnox Search";
    return () => {
      document.title = "Zevnox Search — Executive Search for Trading, FinTech & AI";
    };
  }, []);

  return (
    <div data-testid="privacy-page" className="relative min-h-screen bg-[#0B0B0B] text-white overflow-x-hidden">
      {/* Ambient layers — match site */}
      <div className="pointer-events-none fixed inset-0 -z-10">
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(900px 600px at 85% -5%, rgba(217,249,157,0.07), transparent 60%), radial-gradient(800px 500px at 5% 15%, rgba(167,139,250,0.07), transparent 60%), radial-gradient(900px 600px at 95% 55%, rgba(96,165,250,0.06), transparent 60%)",
          }}
        />
      </div>

      <a href="#main" className="zx-skip-link">Skip to content</a>
      <Header />

      <main id="main" className="pt-32 sm:pt-36 pb-20 sm:pb-24">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12">
          <a
            href="/"
            data-testid="privacy-back-link"
            className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.22em] text-[#D9F99D]/90 hover:text-[#D9F99D] transition-colors"
          >
            <ArrowLeft size={14} aria-hidden="true" /> Back to home
          </a>

          <h1
            data-testid="privacy-heading"
            className="font-display mt-6 text-4xl sm:text-5xl leading-tight tracking-tight text-white"
          >
            Privacy <span className="text-[#D9F99D]">Policy.</span>
          </h1>
          <p className="mt-4 text-sm font-mono uppercase tracking-[0.22em] text-neutral-500">
            Last updated · {LAST_UPDATED}
          </p>

          <div className="mt-12 space-y-10 text-neutral-200 leading-relaxed text-[15px]">
            <Section title="1 · Who we are">
              <p>
                Zevnox Search (&ldquo;<span className="text-white">Zevnox</span>,&rdquo;
                &ldquo;we,&rdquo; &ldquo;us&rdquo;) is a global executive search firm
                specialising in talent for trading, FinTech, AI and digital asset
                organisations. This Privacy Policy explains how we collect, use
                and protect personal information you share with us via{" "}
                <span className="text-white">zevnoxsearch.com</span>.
              </p>
            </Section>

            <Section title="2 · What we collect">
              <p>When you complete a contact form, we collect the personal data
                you choose to provide:</p>
              <ul className="mt-3 space-y-2 list-disc list-inside marker:text-[#D9F99D]/70">
                <li>Name, email address, phone number, and company name.</li>
                <li>The content of your enquiry message.</li>
                <li>Basic technical metadata: IP address (for rate-limit / abuse prevention) and the referring URL or campaign attribution (UTM parameters) if present.</li>
              </ul>
              <p className="mt-4">We do <span className="text-white">not</span> use intrusive trackers, advertising pixels, or third-party fingerprinting on this site.</p>
            </Section>

            <Section title="3 · How we use it">
              <ul className="space-y-2 list-disc list-inside marker:text-[#D9F99D]/70">
                <li>To respond personally to your enquiry within 24 hours.</li>
                <li>To match your role brief or candidacy with relevant
                  opportunities in our network.</li>
                <li>To protect the service from bots, spam and abuse.</li>
                <li>To improve our service quality and understand how visitors
                  discover us.</li>
              </ul>
              <p className="mt-4">We never sell your data and we do not share
                your enquiry with third parties for marketing purposes.</p>
            </Section>

            <Section title="4 · Legal basis">
              <p>For visitors in the UK / EU, our legal bases are{" "}
                <span className="text-white">legitimate interest</span> (running
                an executive search business) and your{" "}
                <span className="text-white">consent</span> when you submit a
                form. You can withdraw consent at any time by emailing us.</p>
            </Section>

            <Section title="5 · How long we keep it">
              <p>Enquiry data is retained for as long as is reasonably necessary
                to support active or prospective mandates — typically{" "}
                <span className="text-white">24 months</span> from last contact.
                Aggregated, non-identifying analytics may be kept longer.</p>
            </Section>

            <Section title="6 · Cookies & similar technologies">
              <p>The site uses only strictly-necessary technical storage
                (e.g. remembering that you closed the privacy banner). We do
                not deploy advertising or cross-site tracking cookies.</p>
            </Section>

            <Section title="7 · Sharing & processors">
              <p>We share your data only with vetted processors that help us
                run the service:</p>
              <ul className="mt-3 space-y-2 list-disc list-inside marker:text-[#D9F99D]/70">
                <li><span className="text-white">MongoDB Atlas</span> — secure
                  data storage.</li>
                <li><span className="text-white">Resend</span> — transactional
                  email delivery for lead notifications.</li>
              </ul>
              <p className="mt-4">All processors operate under strict
                confidentiality and data-processing agreements.</p>
            </Section>

            <Section title="8 · Your rights">
              <p>You have the right to access, correct, delete or restrict the
                processing of your personal data. To exercise these rights,
                email us at{" "}
                <a
                  href="mailto:mayur@zevnoxsearch.com"
                  className="text-[#D9F99D] border-b border-[#D9F99D]/40 hover:border-[#D9F99D] transition-colors"
                >
                  mayur@zevnoxsearch.com
                </a>{" "}
                — we respond within 30 days.</p>
            </Section>

            <Section title="9 · Security">
              <p>All connections are encrypted in transit (HTTPS / TLS 1.2+).
                Data at rest is protected by access-controlled MongoDB Atlas.
                We follow the principle of least privilege for internal access.</p>
            </Section>

            <Section title="10 · International transfers">
              <p>Your data may be processed in regions where our infrastructure
                providers operate (typically EU and US). Standard contractual
                clauses are in place where required.</p>
            </Section>

            <Section title="11 · Updates">
              <p>We may update this policy from time to time. The
                &ldquo;last updated&rdquo; date above will always reflect the
                most recent revision.</p>
            </Section>

            <Section title="12 · Contact">
              <p>For any privacy questions, please write to:{" "}
                <a
                  href="mailto:mayur@zevnoxsearch.com"
                  className="text-[#D9F99D] border-b border-[#D9F99D]/40 hover:border-[#D9F99D] transition-colors"
                >
                  mayur@zevnoxsearch.com
                </a>.
              </p>
            </Section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-display text-xl sm:text-2xl text-white tracking-tight">{title}</h2>
      <div className="mt-3 text-neutral-200 leading-relaxed">{children}</div>
    </section>
  );
}
